/**
 * A4-A11y — assertive live-region helper for toast errors.
 *
 * Sonner's default `aria-live` is "polite" for the whole list. WCAG 4.1.3
 * requires errors to be announced more urgently. We mount a sibling
 * `[role="alert"][aria-live="assertive"]` region in the App shell and
 * push error messages here so screen readers interrupt and announce them.
 *
 * Visual rendering of the toast still belongs to Sonner — this region is
 * sr-only and ONLY drives the announcement.
 */

const REGION_ID = "a11y-error-live-region";

function ensureRegion(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  let el = document.getElementById(REGION_ID);
  if (!el) {
    el = document.createElement("div");
    el.id = REGION_ID;
    el.setAttribute("role", "alert");
    el.setAttribute("aria-live", "assertive");
    el.setAttribute("aria-atomic", "true");
    // sr-only equivalent (avoid Tailwind dependency at module-eval time).
    el.style.position = "absolute";
    el.style.width = "1px";
    el.style.height = "1px";
    el.style.padding = "0";
    el.style.margin = "-1px";
    el.style.overflow = "hidden";
    el.style.clip = "rect(0,0,0,0)";
    el.style.whiteSpace = "nowrap";
    el.style.border = "0";
    document.body.appendChild(el);
  }
  return el;
}

/** Announce an error message via the assertive live region. */
export function announceError(message: string): void {
  const el = ensureRegion();
  if (!el) return;
  // Toggle to force re-announce when the message text is identical to the
  // previous one (some screen readers ignore identical mutations).
  el.textContent = "";
  // microtask ensures the empty -> message diff is observed.
  queueMicrotask(() => {
    el.textContent = message;
  });
}
